import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { useUserToken } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { ToastType } from "@/enums";
import { displayErrorToast, displayToast } from "@/utils/toast";

export interface UpdateWorkflowLabelProtectionRequest {
  labelId: number;
  is_protected: boolean;
  approver_role_id: number | null;
}

export interface UpdateWorkflowLabelProtectionResponse {
  success: boolean;
  workflow_label: {
    id: number;
    name: string;
    workflow_id: number;
    workflow_version_id: number;
    created_at: string;
    is_protected: boolean;
    approver_role_id: number | null;
  };
}

export const useUpdateWorkflowLabelProtection = () => {
  const userToken = useUserToken();
  const queryClient = useQueryClient();

  return useMutation<
    UpdateWorkflowLabelProtectionResponse,
    unknown,
    UpdateWorkflowLabelProtectionRequest
  >({
    mutationFn: async ({ labelId, is_protected, approver_role_id }) => {
      const response = await fetch(`${ENDPOINTS.workflow_labels}/${labelId}/protection`, {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          is_protected,
          approver_role_id,
        }),
      });

      return handleResponse(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ENDPOINTS.workflow_labels],
      });
      queryClient.invalidateQueries({
        queryKey: [ENDPOINTS.workflow_versions],
      });
      displayToast("Label configuration updated successfully", ToastType.success);
    },
    onError: (error: unknown) => {
      let errorMessage: string;
      if (typeof error === "string") {
        errorMessage = error;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      } else {
        errorMessage = "Failed to update workflow label protection";
      }
      displayErrorToast(errorMessage);
    },
  });
};
